import { Grid, Stack, TextField, Typography } from '@mui/material'
import { YesNoChipBox } from '@planning/components/dangerous-goods/YesNoChipBox'
import { useTranslate } from '@tolgee/react'
import { FieldBox, useMinimalsTheme } from '@tom-ui/ui'
import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'

interface IProps {
  isOrderFulfilled: boolean
  identifiableItems?: string[] | null
  onValidationParamsChanged: (params: any) => void
}

export const IdentifiableItemsControl = ({
  isOrderFulfilled,
  identifiableItems,
  onValidationParamsChanged,
}: IProps) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const [hasIdentifiableItems, setHasIdentifiableItems] = useState(false)

  const {
    formState: { errors },
    control,
  } = useFormContext()

  const plannedCargoAmount = useWatch({ control, name: 'plannedCargoAmount' })

  const { fields, replace } = useFieldArray({
    control,
    name: 'identifiableItems',
  })

  useEffect(() => {
    if (identifiableItems && identifiableItems.length > 0) {
      replace(identifiableItems)
      setHasIdentifiableItems(true)
    }
  }, [identifiableItems])

  useEffect(() => {
    if (hasIdentifiableItems) {
      insertIdentifierFields()
    }
  }, [plannedCargoAmount])

  const insertIdentifierFields = () => {
    if (plannedCargoAmount) {
      const items = Array.from(
        { length: Number(plannedCargoAmount) },
        (_, i) => identifiableItems?.[i] || '',
      )
      replace(items)

      onValidationParamsChanged({ unitIds: items })
    }
  }

  return (
    <FieldBox
      titleColor={isOrderFulfilled ? theme.palette.text.disabled : undefined}
      label={t('identifiableItems', 'ID')}
      required
      rightRender={
        <YesNoChipBox
          disableYes={isOrderFulfilled}
          disableNo={isOrderFulfilled}
          activeOption={hasIdentifiableItems ? 'Yes' : 'No'}
          onClickYes={() => {
            setHasIdentifiableItems(true)
            insertIdentifierFields()
          }}
          onClickNo={() => {
            setHasIdentifiableItems(false)
            replace([])

            if (onValidationParamsChanged) {
              onValidationParamsChanged({
                unitIds: [],
              })
            }
          }}
        />
      }
    >
      {hasIdentifiableItems && (
        <Stack gap={1}>
          <Typography variant='caption'>
            {t(
              'identifiableItemsCaption',
              'Please assign unique identifiers to each item. This functionality could be applied only if all items have each unique number',
            )}
          </Typography>
          <Controller
            name='identifiableItems'
            control={control}
            render={() => (
              <Stack gap={1}>
                <Grid container spacing={2}>
                  {fields.map((field, idx) => (
                    <Grid size={3} key={field.id}>
                      <Controller
                        name={`identifiableItems.${idx}`}
                        control={control}
                        defaultValue={identifiableItems?.[idx] || ''}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={`${t('identifiableItems', 'ID')} #${idx + 1}`}
                            disabled={isOrderFulfilled}
                            fullWidth
                            required
                            error={
                              Array.isArray(errors.identifiableItems) &&
                              !!errors.identifiableItems[idx]
                            }
                            helperText={
                              Array.isArray(errors.identifiableItems) &&
                              !!errors.identifiableItems[idx]
                                ? t('fieldIsRequired', 'Field is required.')
                                : ''
                            }
                            onBlur={() => {
                              onValidationParamsChanged({
                                unitIds: fields.map((field, idx) => identifiableItems?.[idx]),
                              })
                            }}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}
          />
        </Stack>
      )}
    </FieldBox>
  )
}
